import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import type { StuutApi } from '@api/api';
import { Model } from '../models';
import type { ListResponse } from '@api/types';
import { QueryParams } from '../query';
import { DEFAULT_TABLE_PAGE_SIZE } from '@stuut/common/src/constants';

export abstract class Resource {
  api: StuutApi;

  resourcePath: string;

  constructor(api: StuutApi) {
    this.api = api;
  }

  path(...args: (string | number)[]): string {
    args = args.filter((arg) => Boolean(arg));
    if (args.length) {
      return `${this.resourcePath}/${args.join('/')}`;
    }

    return this.resourcePath;
  }
}

export interface PaginatedResult<F> {
  data: F[];

  current_page: number;

  next_page: number | null;

  total_records: number;
}

export abstract class ModelResource<T extends Model, CreateData = any> extends Resource {
  model: (new (...args: any) => T) & { fromJson(json: any): T };

  async create(data?: CreateData, args?: Omit<AxiosRequestConfig, 'data'>): Promise<T> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return this.api.post(this.path(), { data, ...args }).then((r) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      return this.model.fromJson(r.data) as T;
    });
  }

  async list(args?: AxiosRequestConfig, paginatedResult?: false): Promise<T[]>;

  async list(args?: AxiosRequestConfig, paginatedResult?: true): Promise<PaginatedResult<T>>;

  async list(
    args?: AxiosRequestConfig,
    paginatedResult?: boolean,
  ): Promise<T[] | PaginatedResult<T>> {
    let params = args?.params;

    // If plain object, apply defaults
    if (params && !(params instanceof QueryParams)) {
      params = {
        page: 0,
        limit: DEFAULT_TABLE_PAGE_SIZE,
        ...params,
      };

      // Portal API uses 'istatus[in][]' format for status arrays
      if (params.istatus && Array.isArray(params.istatus)) {
        params.istatus = { in: params.istatus };
      }
    }

    // If QueryParams, serialize it
    if (params instanceof QueryParams) {
      params = params.serialize({ forApiRequest: true });
    }

    const response = await this.api.get(this.path(), { ...args, params });

    // Auto-detect response format
    const isPlainArray = Array.isArray(response.data);
    const rawData = isPlainArray ? response.data : response.data.data || [];

    // Deserialize to model instances
    const serializedData = rawData.map((json: any) => this.model.fromJson(json) as T);

    // Return based on paginatedResult parameter
    if (paginatedResult) {
      if (isPlainArray) {
        return {
          data: serializedData,
          total_records: serializedData.length,
          current_page: 0,
          next_page: null,
        } as PaginatedResult<T>;
      } else {
        return { ...response.data, data: serializedData } as PaginatedResult<T>;
      }
    }

    return serializedData;
  }

  async listWithPageInfo(args?: AxiosRequestConfig): Promise<{
    data: T[];
    pageInfo: {
      current_page?: number;
      next_page?: number;
      total_records?: number;
    };
  }> {
    if (args?.params instanceof QueryParams) {
      args.params = args.params.serialize({ forApiRequest: true });
    }

    return this.api.get(this.path(), args).then((r: AxiosResponse<ListResponse>) => {
      const { data, ...pageInfo } = r.data;

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        data: data.map((json: any) => this.model.fromJson(json) as T),
        pageInfo: {
          current_page: pageInfo.current_page,
          next_page: pageInfo.next_page,
          total_records: pageInfo.total_records,
        },
      };
    });
  }

  async retrieve(entity_id?: number | string, entity?: T): Promise<T> {
    return this.api.get(this.path(entity_id || '')).then((r) => {
      if (entity && entity instanceof Model) {
        entity.updateFromJson(r.data);
        return entity;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      return this.model.fromJson(r.data) as T;
    });
  }

  async update(entity_id: number | string, args?: AxiosRequestConfig, entity?: T): Promise<T> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return this.api.post(this.path(entity_id), args).then((r) => {
      if (entity && entity instanceof Model) {
        entity.updateFromJson(r.data);
        return entity;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      return this.model.fromJson(r.data) as T;
    });
  }

  async patch(entity_id: number | string, args?: AxiosRequestConfig, entity?: T): Promise<any> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return this.api.patch(this.path(entity_id), args).then((r) => {
      if (entity && entity instanceof Model) {
        entity.updateFromJson(r.data);
        return entity;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      return this.model.fromJson(r.data) as T;
    });
  }

  async delete(entity_id: number | string, args?: AxiosRequestConfig): Promise<any> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return this.api.delete(this.path(entity_id), args);
  }
}
